/**
 * Client for the TheGoldsmith integration (server/modules/integrations/goldsmith).
 * The workspace's Goldsmith key never reaches the browser: every call here goes
 * to OUR server, which holds it encrypted and talks to Goldsmith itself.
 */
import { apiDelete, apiGet, apiPost, apiPut } from "../shared/apiClient";

export type GoldsmithFeature = "photoshoot" | "video" | "production";
export type FixerFunction = "swap-diamonds" | "metal-color" | "clean-background";
export const FIXER_CUTS = ["round", "brilliant", "princess", "oval", "cushion", "emerald", "pear", "radiant", "marquise", "asscher", "heart", "baguette"] as const;
export type FixerCut = (typeof FIXER_CUTS)[number];

export interface GoldsmithStatus {
  connected: boolean;
  origin: string;
  /** Jewelry Fixer needs Goldsmith's partner API to expose it; false until then. */
  fixerAvailable: boolean;
  keyPreview?: string | null;
  connectedAt?: string | null;
  name?: string;
  features?: string[];
  dailyCreditCap?: number;
  creditsUsedToday?: number;
  creditsRemainingToday?: number | null;
  /** Set when the key is stored but Goldsmith could not be reached / rejected it. */
  error?: string;
}

export interface GoldsmithTheme {
  themeId: string;
  name: string;
  description: string;
  previewUrl: string | null;
  credits: number | null;
  creditsUnit: "image" | "second" | null;
  options: { resolutions?: string[] | null; qualities?: string[] | null } | null;
  video: {
    engine?: string;
    durations?: number[] | null;
    defaultDuration?: number | null;
    resolutions?: string[] | null;
    aspectRatios?: string[] | null;
    supportsLastImage?: boolean;
  } | null;
}

export interface GoldsmithRender {
  mediaType: "image/png" | "image/jpeg" | "image/webp";
  /** Plain base64. */
  data: string;
}

export interface GoldsmithJobRequest {
  feature: GoldsmithFeature;
  images: GoldsmithRender[];
  endFrame?: GoldsmithRender;
  themeId?: string;
  themeName?: string;
  prompt?: string;
  params?: {
    aspectRatio?: string;
    resolution?: string;
    quality?: "low" | "medium" | "high";
    duration?: number;
    functionType?: FixerFunction;
    diamonds?: { cut: FixerCut }[];
    diamondCount?: number;
  };
  idempotencyKey: string;
}

export interface GoldsmithOutput {
  assetId: string | null;
  url: string;
  mimeType: string;
}

export interface GoldsmithJobView {
  jobId: string;
  feature: string;
  status: "queued" | "processing" | "completed" | "failed" | "cancelled";
  percent: number;
  steps: { label: string; status: string }[];
  error: string | null;
  creditsCharged: number | null;
  outputs: GoldsmithOutput[];
  savedToVault: boolean;
}

export const getGoldsmithStatus = () => apiGet<GoldsmithStatus>("/integrations/goldsmith");
export const connectGoldsmith = (apiKey: string) => apiPut<GoldsmithStatus>("/integrations/goldsmith", { apiKey });
export const disconnectGoldsmith = () => apiDelete<GoldsmithStatus>("/integrations/goldsmith");
export const listGoldsmithThemes = (feature: GoldsmithFeature, category = "rings", functionType?: FixerFunction) =>
  apiGet<GoldsmithTheme[]>(
    `/integrations/goldsmith/themes?feature=${feature}&category=${encodeURIComponent(category)}${functionType ? `&functionType=${functionType}` : ""}`,
  );
export const sendToGoldsmith = (body: GoldsmithJobRequest) =>
  apiPost<{ jobId: string; estimatedCredits: number; replayed: boolean }>("/integrations/goldsmith/jobs", body);
export const getGoldsmithJob = (jobId: string) => apiGet<GoldsmithJobView>(`/integrations/goldsmith/jobs/${encodeURIComponent(jobId)}`);

export interface GoldsmithJobSummary {
  jobId: string;
  feature: GoldsmithFeature;
  themeName: string | null;
  status: string;
  creditsCharged: number | null;
  estimatedCredits: number | null;
  outputs: { assetId: string; url: string; mimeType: string }[];
  createdAt: string;
}

export const listGoldsmithJobs = () => apiGet<GoldsmithJobSummary[]>("/integrations/goldsmith/jobs");
